/* eslint-disable @typescript-eslint/no-redeclare */
import { useTheme } from '@emotion/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Divider, Typography, useMediaQuery } from '@mui/material';
import PasswordField from 'components/Common/LabelledTextField/PasswordField';
import CommonDialog from 'components/CommonDialog';
import Enums from 'constants/Enums';
import trasnlatedFormValidationSchemas from 'FormValidations/translatedFormValidationSchemas';
import { getLocalStorageItem } from 'hooks';
import { useResendCodeQuery } from 'queries/AuthQueries';
import { useUpdatePinApiQuery } from 'queries/ChangePin';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import HandleErrorResponse from 'utils/HandleErrorResponse';
import * as yup from 'yup';

import ForgotPinPopup from './ForgotPinPopup';
import SetupPinPopup from './SetupPinPopup';

const ChangePin: React.FC = () => {
  const { t } = useTranslation('account');
  const { t: translation, i18n } = useTranslation('validationErrorMessages');
  const theme: any = useTheme();
  const underSm = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentPin, newPin, confirmPin } = useMemo(() => {
    return trasnlatedFormValidationSchemas(translation);
  }, [translation, i18n.language]);

  const [open, setOpen] = useState<boolean>(false);
  const [pinScreen, setPinScreen] = useState<boolean>(false);
  const { username } = getLocalStorageItem('roles');
  const [modalDetails, setModalDetails] = useState<string>('');
  const { mutate: resendCodeApi } = useResendCodeQuery();
  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    const payload: any = {
      sendOn: username,
      eventCode: Enums.EventType.PIN_RESET,
    };
    resendCodeApi(payload, {
      onSuccess: (res: any) => {
        setModalDetails(res?.data?.message.split(','));
        setOpen(true);
      },
    });
  };
  const { mutate: updatePinApi } = useUpdatePinApiQuery();
  const [buttonEnable, setButtonEnable] = useState<boolean>(false);

  const userPinSchema = yup.object().shape({
    currentPin,
    newPin,
    confirmPin,
  });

  const {
    register,
    handleSubmit,
    reset,
    trigger,
    watch,
    setError,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(userPinSchema),
  });
  const handleResetInfo = () => {
    reset({
      currentPin: '',
      newPin: '',
      confirmPin: '',
    });
  };
  // handlers
  const submitAuthForm = (data: any) => {
    const updatePayload: any = {
      currentPin: data?.currentPin.toString().trim(),
      newPin: data?.newPin.toString().trim(),
      confirmPin: data?.confirmPin.toString().trim(),
    };
    updatePinApi(updatePayload, {
      onError: (err: any) => {
        HandleErrorResponse(err, setError);
      },
    });
  };

  const [newpin, confirmpin] = watch(['newPin', 'confirmPin']);
  useEffect(() => {
    if (confirmpin) {
      trigger('confirmPin');
    }
  }, [newpin, trigger]);
  const handleAuthErrors = (err: any) => {};
  return (
    <div className="pt-2">
      <form onSubmit={handleSubmit(submitAuthForm, handleAuthErrors)}>
        <div className="mb-5">
          <Typography fontSize={18} fontWeight={600} color="font.main">
            {t('pin')}
          </Typography>

          <Typography variant="subtitle2" fontWeight={400} color="font.900">
            {t('changePinInstruction')}
          </Typography>
        </div>
        <Divider />

        <div className="mb-5 mt-6 flex flex-col gap-4 md:flex-row md:items-start">
          <Typography
            variant="subtitle2"
            fontWeight={500}
            color="font.main"
            className="md:w-[280px]"
          >
            {t('currentPIN')} *
          </Typography>
          <div className="w-full md:w-[512px]">
            <PasswordField
              label=""
              placeholder={`${t('enterCurrentPIN')}`}
              {...register('currentPin')}
              error={Boolean(errors.currentPin)}
              helperText={errors?.currentPin?.message}
              onBlur={() => setButtonEnable(true)}
            />
            <div className="mt-1.5 cursor-pointer" onClick={handleOpen}>
              <Typography variant="subtitle2" color="font.900" fontWeight={400}>
                {t('forgotPin')}
              </Typography>
            </div>
          </div>
        </div>

        <Divider />

        <div className="mb-5 mt-6 flex flex-col gap-4 md:flex-row md:items-start">
          <Typography
            variant="subtitle2"
            fontWeight={500}
            color="font.main"
            className="md:w-[280px]"
          >
            {t('newPin')} *
          </Typography>
          <div className="w-full md:w-[512px]">
            <PasswordField
              label=""
              placeholder={`${t('enterNewPin')}`}
              {...register('newPin')}
              error={Boolean(errors.newPin)}
              helperText={errors?.newPin?.message}
              onBlur={() => setButtonEnable(true)}
            />
            <div className="mt-1.5 cursor-default">
              <Typography variant="subtitle2" color="font.900" fontWeight={400}>
                {t('addFourDigitNewPin')}
              </Typography>
            </div>
          </div>
        </div>

        <Divider />

        <div className="my-6 flex flex-col gap-4 md:flex-row md:items-start">
          <Typography
            variant="subtitle2"
            fontWeight={500}
            color="font.main"
            className="md:w-[280px]"
          >
            {t('confirmNewPin')} *
          </Typography>
          <div className="w-full md:w-[512px]">
            <PasswordField
              label=""
              placeholder={`${t('enterConfirmPIN')}`}
              {...register('confirmPin')}
              error={Boolean(errors.confirmPin)}
              helperText={errors?.confirmPin?.message}
              onBlur={() => setButtonEnable(true)}
            />
          </div>
        </div>

        <div className={`${underSm ? 'mb-[50px]' : ''} flex justify-end pr-2`}>
          <div className="flex gap-3">
            <Button
              variant="outlined"
              color="font"
              onClick={handleResetInfo}
              disabled={!buttonEnable}
              className={` ${
                !buttonEnable ? 'cursor-not-allowed !bg-[#90cdf4]' : ''
              }`}
            >
              {t('cancelButtonText')}
            </Button>

            <Button
              type="submit"
              disabled={!buttonEnable}
              className={`bg-[#11AC9E] hover:bg-[#90BEE6] ${
                !buttonEnable ? 'cursor-not-allowed !bg-[#90cdf4]' : ''
              }`}
            >
              {t('updatePIN')}
            </Button>
          </div>
        </div>
      </form>

      <CommonDialog maxWidth="sm" open={open} onClose={handleClose}>
        {pinScreen ? (
          <SetupPinPopup setOpen={setOpen} setPinScreen={setPinScreen} />
        ) : (
          <ForgotPinPopup
            modalDetails={modalDetails}
            setOpen={setOpen}
            handleNewPinScreen={(val: any) => {
              setPinScreen(val);
            }}
          />
        )}{' '}
      </CommonDialog>
    </div>
  );
};

export default ChangePin;
